/**
 * Country split panel - QuickBooks-sourced, trailing-12-month
 * net-of-VAT totals grouped by counterparty billing-address country.
 * Used twice: turnover by client country (Clients tab) and
 * expenditure by supplier country (Creditors tab). Blank countries
 * are assumed UK and disclosed.
 */
import { useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import { ChevronRight } from "lucide-react";
import { PanelTitle } from "../staff/CommissionedPanels";
import { Card, InfoTip } from "./cfoShared";
import { useQboData, fmtGbp } from "./qboShared";

const PALETTE = ["#1B4F8A", "#2a9d8f", "#d4a373", "#e76f51", "#8b5cf6", "#a8a29e", "#d6d3d1"];

function CountryRow({ c, testid, countKey, countNoun }) {
  const [open, setOpen] = useState(false);
  const slug = c.country.toLowerCase().replace(/[^a-z]+/g, "-");
  const companies = c.company_list || [];
  const expandable = companies.length > 0;
  return (
    <li data-testid={`${testid}-row-${slug}`}>
      <button
        type="button"
        onClick={() => expandable && setOpen((v) => !v)}
        data-testid={`${testid}-row-toggle-${slug}`}
        className={[
          "w-full flex items-baseline justify-between gap-3 text-left rounded",
          expandable ? "cursor-pointer hover:bg-stone-50" : "cursor-default",
        ].join(" ")}
      >
        <span className="text-xs text-slate-700 inline-flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
          {c.country}
          {c.companies != null ? (
            <span className="text-[10px] text-stone-400">
              ({c.companies} {c.companies === 1 ? "company" : "companies"} ·{" "}
              {c[countKey]} project{c[countKey] === 1 ? "" : "s"})
            </span>
          ) : (
            <span className="text-[10px] text-stone-400">
              ({c[countKey]} {countNoun}
              {c[countKey] === 1 ? "" : "s"})
            </span>
          )}
          {expandable && (
            <ChevronRight
              className={`w-3 h-3 text-stone-400 transition-transform ${open ? "rotate-90" : ""}`}
            />
          )}
        </span>
        <span className="text-xs font-mono tabular-nums text-slate-900">
          <strong>{c.pct != null ? `${c.pct}%` : "–"}</strong>
          <span className="text-stone-400 ml-2">{fmtGbp(c.net)}</span>
        </span>
      </button>
      {open && (
        <ul
          data-testid={`${testid}-companies-${slug}`}
          className="mt-1 mb-2 ml-5 max-h-44 overflow-y-auto pr-1 space-y-0.5 border-l border-stone-200 pl-3"
        >
          {companies.map((co) => (
            <li
              key={co.name}
              className="flex items-baseline justify-between gap-3 text-[11px]"
            >
              <span className={co.named ? "text-slate-600" : "text-stone-400 italic"}>
                {co.name}
                {!co.named && " · not on client list"}
                {co.projects > 1 && (
                  <span className="text-stone-400 not-italic"> · {co.projects} projects</span>
                )}
              </span>
              <span className="font-mono tabular-nums text-stone-500 shrink-0">
                {fmtGbp(co.net)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function CountrySplit({
  path = "turnover-by-country",
  title = "Turnover by country · last 12 months",
  infoId = "cl_country",
  countKey = "customers",
  countNoun = "client",
  testid = "country-split-card",
}) {
  const { data, loading, error } = useQboData(path);
  if (loading || error) {
    return (
      <Card testid={testid}>
        <PanelTitle>{title}</PanelTitle>
        <p className="text-xs text-stone-400 mt-2">
          {loading ? "Loading from QuickBooks…" : `QuickBooks unavailable: ${error}`}
        </p>
      </Card>
    );
  }
  const rows = data.countries || [];
  const chart = rows.map((r, i) => ({ ...r, color: PALETTE[i % PALETTE.length] }));
  return (
    <Card testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PanelTitle>
          {title} <InfoTip id={infoId} />
        </PanelTitle>
        <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
          QuickBooks · net of VAT
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center mt-2">
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chart.filter((c) => c.net > 0)}
                dataKey="net"
                nameKey="country"
                innerRadius={54}
                outerRadius={82}
                paddingAngle={2}
                strokeWidth={0}
              >
                {chart.filter((c) => c.net > 0).map((c) => (
                  <Cell key={c.country} fill={c.color} />
                ))}
              </Pie>
              <ReTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                formatter={(v, n, p) => [`${fmtGbp(v)} (${p.payload.pct}%)`, n]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <ul className="space-y-1.5">
            {chart.map((c) => (
              <CountryRow
                key={c.country}
                c={c}
                testid={testid}
                countKey={countKey}
                countNoun={countNoun}
              />
            ))}
          </ul>
          <p className="text-[10px] text-stone-400 mt-3 leading-snug">
            Total {fmtGbp(data.total_net)} net of VAT,{" "}
            {new Date(data.window?.from).toLocaleDateString("en-GB", { month: "short", year: "numeric" })} to today.
            {data.assumed_uk_net > 0 && (
              <>
                {" "}
                {fmtGbp(data.assumed_uk_net)} is from {countNoun}s with no country anywhere in their address - assumed United Kingdom.
              </>
            )}
            {data.inferred_net > 0 && (
              <>
                {" "}
                {fmtGbp(data.inferred_net)} had a blank country field but the country was read from the address text.
              </>
            )}
            {data.registry_net > 0 && (
              <>
                {" "}
                {fmtGbp(data.registry_net)} matched via the uploaded client-numbers list.
              </>
            )}
            {rows.some((r) => (r.company_list || []).length > 0) && (
              <>
                {" "}
                Click a country to see its client companies - project codes are grouped by their client number and named from the client list.
              </>
            )}
            {data.excluded_incentives_net > 0 && (
              <>
                {" "}
                {fmtGbp(data.excluded_incentives_net)} of payments to incentive-named suppliers (respondent payouts) is excluded.
              </>
            )}
          </p>
        </div>
      </div>
    </Card>
  );
}
